import React, { type JSX, type ReactNode, type SVGProps, useState } from 'react';
import * as DropdownMenu from '@radix-ui/react-dropdown-menu';
import classNames from 'classnames';
import { MacScrollbar } from 'mac-scrollbar';
import { AnimatePresence, motion } from 'motion/react';
import Button from '@/shared/components/flip-ui-kit/Button';
import useScreenSize from '@/shared/hooks/useScreenSize';
import ChevronIcon from '@/shared/icons/flip-ui-kit/large/Chevron';
import FilterIcon from '@/shared/icons/flip-ui-kit/small/Filter';
import { defaultAnimationProps } from '@/shared/utils/consts';

type Filter<T> = {
  type: T;
  label: string;
  icon: (props: SVGProps<SVGSVGElement>) => JSX.Element;
};
export type Filters<T> = readonly Filter<T>[];

export const QuickAction = ({
  text,
  onClick,
  className,
}: {
  text: string;
  onClick: () => void;
  className?: string;
}) => (
  <span
    className={classNames('ml-auto cursor-pointer text-cf-green-3', className)}
    onClick={onClick}
  >
    {text}
  </span>
);

const ActiveFilterLayout = <T,>({
  activeFilter,
  setActiveFilter,
  allFilters,
  selectedFilter,
  quickAction,
}: {
  activeFilter: keyof T;
  setActiveFilter: (value: keyof T | null) => void;
  allFilters: Filters<keyof T>;
  selectedFilter: (type: Filters<keyof T>[number]['type']) => ReactNode;
  quickAction?: (activeFilter: keyof T) => ReactNode;
}) => (
  <motion.div id={activeFilter.toString()} {...defaultAnimationProps}>
    <div className="flex h-[40px] items-center gap-x-2 border-b border-cf-gray-4 p-3">
      <div
        className="flex items-center gap-x-1 hover:cursor-pointer"
        onClick={() => setActiveFilter(null)}
      >
        <ChevronIcon className="text-cf-light-4" orientation="left-right" flip />
        <div className="text-cf-light-4">
          {allFilters.find((filter) => filter.type === activeFilter)?.label}
        </div>
      </div>
      {quickAction?.(activeFilter)}
    </div>
    <MacScrollbar skin="dark" className="max-h-[384px] overflow-y-scroll">
      {selectedFilter(activeFilter)}
    </MacScrollbar>
  </motion.div>
);

const FilterDropdown = <T,>({
  isOpen,
  setIsOpen,
  resetAllFilters,
  hasActiveFilters,
  allFilters,
  selectedFilter,
  quickAction,
  renderInlineControl,
}: {
  isOpen: boolean;
  setIsOpen: (value: boolean) => void;
  resetAllFilters: () => void;
  hasActiveFilters: boolean;
  allFilters: Filters<keyof T>;
  selectedFilter: (type: Filters<keyof T>[number]['type']) => ReactNode;
  quickAction?: (activeFilter: keyof T) => ReactNode;
  // When provided and it returns a node for a filter, that filter renders the
  // node inline (e.g. a checkbox) instead of drilling into a sub-panel.
  renderInlineControl?: (type: Filters<keyof T>[number]['type']) => ReactNode;
}) => {
  const { isMobile } = useScreenSize();
  const [activeFilter, setActiveFilter] = useState<(typeof allFilters)[number]['type'] | null>(
    null,
  );

  return (
    <DropdownMenu.Root
      open={isOpen}
      onOpenChange={(value) => {
        setIsOpen(value);
        setActiveFilter(null);
      }}
      modal={false}
    >
      <DropdownMenu.Trigger asChild>
        <Button
          type="secondary-standard"
          className={classNames(
            'group !min-w-fit font-aeonikMedium !text-12 hover:enabled:text-cf-white hover:enabled:![background:linear-gradient(180deg,rgba(255,255,255,0.02)_0%,rgba(255,255,255,0.00)_100%),#2B2B2B]',
            isOpen &&
              'border-[#292929] shadow-[0px_0px_0px_1.5px_rgba(58,58,58,0.36),0px_0px_0px_1px_rgba(255,255,255,0.24)] [background:linear-gradient(180deg,rgba(255,255,255,0.02)_0%,rgba(255,255,255,0.00)_100%),#2B2B2B]',
          )}
          size="small"
        >
          <span className="flex items-center gap-x-0.5" tabIndex={-1}>
            <FilterIcon />
            {isMobile ? 'Filter' : 'Add filter'}
          </span>
          <ChevronIcon
            flip={!isOpen}
            className={classNames('text-cf-light-3 transition group-hover:text-cf-white')}
          />
        </Button>
      </DropdownMenu.Trigger>
      <DropdownMenu.Portal>
        <DropdownMenu.Content
          className="z-10 w-full min-w-[222px] rounded-md border border-cf-gray-4 bg-cf-gray-3-5 font-aeonikMedium text-13 shadow-grayPop2"
          sideOffset={8}
          align="start"
        >
          <AnimatePresence mode="wait">
            {!activeFilter ? (
              <motion.div
                className="flex flex-col font-aeonikMedium text-13"
                {...defaultAnimationProps}
              >
                <div className="flex h-[40px] items-center gap-x-2 border-b border-cf-gray-4 p-3">
                  <div className="text-cf-light-4">Add filter</div>
                  <span
                    className={classNames(
                      'ml-auto cursor-pointer text-cf-green-3',
                      !hasActiveFilters && 'hidden',
                    )}
                    onClick={() => resetAllFilters()}
                  >
                    Reset
                  </span>
                </div>
                {allFilters.map((filter) => {
                  const inlineControl = renderInlineControl?.(filter.type);
                  const Icon = filter.icon;

                  if (inlineControl) {
                    return (
                      <div
                        key={filter.type.toString()}
                        className="flex h-[48px] items-center gap-x-2 p-3 transition duration-100 ease-in-out hover:bg-cf-gray-4"
                      >
                        <span className="text-cf-light-2">
                          <Icon className="h-[24px] w-[24px]" />
                        </span>
                        <label
                          htmlFor={`filter-${filter.type.toString()}`}
                          className="text-cf-white hover:cursor-pointer"
                        >
                          {filter.label}
                        </label>
                        <span className="ml-auto">{inlineControl}</span>
                      </div>
                    );
                  }

                  return (
                    <div
                      key={filter.type.toString()}
                      className="flex h-[48px] items-center gap-x-2 p-3 transition duration-100 ease-in-out hover:cursor-pointer hover:bg-cf-gray-4"
                      onClick={() => setActiveFilter(filter.type)}
                    >
                      <span className="text-cf-light-2">
                        <Icon className="h-[24px] w-[24px]" />
                      </span>
                      <span className="text-cf-white">{filter.label}</span>
                      <ChevronIcon className="ml-auto" orientation="left-right" />
                    </div>
                  );
                })}
              </motion.div>
            ) : (
              <ActiveFilterLayout
                activeFilter={activeFilter}
                setActiveFilter={setActiveFilter}
                allFilters={allFilters}
                selectedFilter={selectedFilter}
                quickAction={quickAction}
              />
            )}
          </AnimatePresence>
        </DropdownMenu.Content>
      </DropdownMenu.Portal>
    </DropdownMenu.Root>
  );
};

export default FilterDropdown;
